import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { CheckCircle2, Ban, StickyNote, Eye } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { VacationRequest, VacationRequestStatus, Member } from '../../types';
import { VacationStatusBadge } from './VacationStatusBadge';
import { MemberCell } from './MemberCell';
import { formatShortDate } from '../../utils/formatters';

interface RequestsTableProps {
  requests: VacationRequest[];
  members?: Member[];
  loading?: boolean;
  /** Current user id — a manager can't review their own request. */
  currentUserId?: string;
  /** Optional filter controls rendered in the card header. */
  filters?: React.ReactNode;
  /** Optional footer (e.g. pagination) rendered below the table. */
  footer?: React.ReactNode;
  onReview?: (request: VacationRequest) => void;
  /** Read-only preview of any request (dates, requested-on, comments). */
  onView?: (request: VacationRequest) => void;
  /** Cancel one's own pending request. */
  onCancel?: (request: VacationRequest) => void;
}

/**
 * Manager/admin "Requests" panel: a titled card listing team requests (any
 * status) with optional status/member filters. Every row exposes an action:
 * pending requests from others can be reviewed, one's own pending request can
 * be cancelled, and any other request opens a read-only preview.
 */
export const RequestsTable: React.FC<RequestsTableProps> = ({
  requests,
  members = [],
  loading,
  currentUserId,
  filters,
  footer,
  onReview,
  onView,
  onCancel,
}) => {
  const { t } = useTranslation('vacations');

  const memberById = React.useMemo(() => {
    const map = new Map<string, Member>();
    members.forEach((m) => map.set(m.id, m));
    return map;
  }, [members]);

  return (
    <Card data-testid="vacations-requests">
      <CardHeader className="space-y-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          {t('approvals.requests.title')}
          {!loading && requests.length > 0 && (
            <span
              className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-2 text-xs font-semibold text-muted-foreground"
              data-testid="vacations-requests-count"
            >
              {requests.length}
            </span>
          )}
        </CardTitle>
        {filters}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('approvals.requests.headers.employee')}</TableHead>
              <TableHead>{t('approvals.requests.headers.dates')}</TableHead>
              <TableHead>{t('table.headers.workingDays')}</TableHead>
              <TableHead>{t('table.headers.status')}</TableHead>
              <TableHead className="text-right">{t('table.headers.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  {t('table.loading')}
                </TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  {t('approvals.requests.empty')}
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => {
                const member = memberById.get(request.memberId);
                const isPending = request.status === VacationRequestStatus.PENDING;
                const isOwnRequest = !!currentUserId && request.memberId === currentUserId;
                const canReview = isPending && !isOwnRequest;
                const canCancel = isPending && isOwnRequest;
                const hasRejectionComment =
                  request.status === VacationRequestStatus.REJECTED && !!request.reviewerComments;
                return (
                  <TableRow key={request.id} data-testid={`vacation-request-row-${request.id}`}>
                    <TableCell>
                      <MemberCell
                        name={member?.name ?? request.memberId}
                        subtitle={member?.department?.name ?? member?.jobTitle}
                        imageUrl={member?.profilePictureUrl}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatShortDate(request.startDate)} – {formatShortDate(request.endDate)}
                    </TableCell>
                    <TableCell>{request.workingDays}</TableCell>
                    <TableCell>
                      <div className="inline-flex items-center gap-1.5">
                        <VacationStatusBadge status={request.status} />
                        {hasRejectionComment && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <StickyNote className="h-4 w-4 cursor-help text-destructive" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-sm">
                                  {t('card.reviewerComment')}: {request.reviewerComments}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {canReview ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onReview?.(request)}
                            data-testid={`vacation-review-${request.id}`}
                          >
                            <CheckCircle2 className="mr-1 h-4 w-4 text-green-600" />
                            {t('actions.review')}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onView?.(request)}
                            data-testid={`vacation-view-${request.id}`}
                          >
                            <Eye className="mr-1 h-4 w-4" />
                            {t('actions.view')}
                          </Button>
                        )}
                        {canCancel && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive focus:text-destructive"
                            onClick={() => onCancel?.(request)}
                            data-testid={`vacation-cancel-${request.id}`}
                          >
                            <Ban className="mr-1 h-4 w-4" />
                            {t('actions.cancel')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {footer}
      </CardContent>
    </Card>
  );
};
